import type { CreateProjectInput } from '@idc-forms/schemas'
import { CircleOff, CirclePlay, FileText, Info, Pencil, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { useNavigate } from 'react-router'
import { PATHS } from '@/app/router/routes'
import { Permission } from '@/features/roles/consts/permissions'
import { DetailFieldRow } from '@/shared/components/detail-rows/detail-field-row'
import { UserChip } from '@/shared/components/detail-rows/user-chip'
import { SidePanelActions } from '@/shared/components/side-panel/side-panel-actions'
import {
  SidePanelSection,
  SidePanelSectionBody
} from '@/shared/components/side-panel/side-panel-blocks'
import { usePermissions } from '@/shared/hooks/use-permissions'
import { useSidePanelStore } from '@/shared/store/side-panel.store'
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/shared/ui/accordion'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/shared/ui/alert-dialog'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/ui/tabs'
import { formatHumanDateTime } from '@/shared/utils/format-date'
import { useDeleteProjectMutation, useUpdateProjectMutation } from '../hooks/use-project-mutations'
import type { ProjectAdminRow } from '../models/project-admin'
import { ProjectLogo } from './project-badge'
import { ProjectForm } from './project-form'
import { ProjectActiveBadge, ProjectCurrencyBadge } from './projects-admin-columns'

type ProjectDetailPanelProps = {
  project: ProjectAdminRow
}

/**
 * Panel de detalle del proyecto: modo VISTA (resumen + acciones + tab "Más
 * información" con acordeón de actividad) y modo FORMULARIO in-place (mismo
 * ProjectForm que la creación). "Plantillas de documentos" navega al editor
 * de plantillas del proyecto (página completa que se conserva).
 */
export function ProjectDetailPanel({ project }: ProjectDetailPanelProps) {
  const [mode, setMode] = useState<'view' | 'edit'>('view')
  const [confirmDelete, setConfirmDelete] = useState(false)
  const closePanel = useSidePanelStore((s) => s.closePanel)
  const navigate = useNavigate()
  const updateMutation = useUpdateProjectMutation()
  const deleteMutation = useDeleteProjectMutation()
  const { has } = usePermissions()

  const handleUpdate = (values: CreateProjectInput) => {
    updateMutation.mutate(
      { id: project._id, payload: values },
      {
        onSuccess: () => {
          setMode('view')
        }
      }
    )
  }

  const toggleActive = () => {
    updateMutation.mutate({ id: project._id, payload: { active: !project.active } })
  }

  if (mode === 'edit') {
    return (
      <ProjectForm
        defaultValues={{
          name: project.name,
          code: project.code,
          society: project.society,
          currency: project.currency,
          image: project.image ?? undefined,
          requiresAuthorization: project.requiresAuthorization ?? false,
          authorizerIds: project.authorizerIds ?? [],
          active: project.active
        }}
        submitLabel="Guardar cambios"
        pending={updateMutation.isPending}
        onSubmit={handleUpdate}
        onCancel={() => {
          setMode('view')
        }}
      />
    )
  }

  return (
    <div className="flex flex-col">
      <SidePanelSection>
        <SidePanelSectionBody className="gap-3">
          <div className="flex items-center gap-3">
            <ProjectLogo name={project.name} code={project.code} className="h-9 w-9" />
            <div className="min-w-0">
              <h3 className="truncate text-base font-semibold leading-tight">{project.name}</h3>
              {project.societyName && (
                <p className="truncate text-sm text-muted-foreground">{project.societyName}</p>
              )}
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <DetailFieldRow label="Estado" value={<ProjectActiveBadge active={project.active} />} />
            <DetailFieldRow label="Código" value={project.code} copyValue={project.code} />
            <DetailFieldRow
              label="Moneda"
              value={<ProjectCurrencyBadge currency={project.currency} />}
            />
            <DetailFieldRow
              label="Requiere autorización"
              value={
                project.requiresAuthorization
                  ? `Sí · ${project.authorizerIds?.length ?? 0} autorizador(es)`
                  : 'No'
              }
            />
          </div>
        </SidePanelSectionBody>
      </SidePanelSection>

      <SidePanelSection>
        <SidePanelActions
          maxInline={2}
          actions={[
            ...(has(Permission.UpdateProjects)
              ? [
                  {
                    label: 'Editar proyecto',
                    icon: <Pencil className="h-4 w-4" />,
                    variant: 'default' as const,
                    primary: true,
                    onClick: () => {
                      setMode('edit')
                    }
                  },
                  {
                    label: 'Plantillas de documentos',
                    icon: <FileText className="h-4 w-4" />,
                    variant: 'outline' as const,
                    onClick: () => {
                      closePanel()
                      void navigate(PATHS.admin.business.projects.documents(project._id))
                    }
                  },
                  {
                    label: project.active ? 'Desactivar proyecto' : 'Activar proyecto',
                    icon: project.active ? (
                      <CircleOff className="h-4 w-4" />
                    ) : (
                      <CirclePlay className="h-4 w-4" />
                    ),
                    variant: 'outline' as const,
                    disabled: updateMutation.isPending,
                    onClick: toggleActive
                  }
                ]
              : []),
            ...(has(Permission.DeleteProjects)
              ? [
                  {
                    label: 'Eliminar proyecto',
                    icon: <Trash2 className="h-4 w-4" />,
                    variant: 'destructive' as const,
                    disabled: deleteMutation.isPending,
                    onClick: () => {
                      setConfirmDelete(true)
                    }
                  }
                ]
              : [])
          ]}
        />
      </SidePanelSection>

      <Tabs defaultValue="more-info">
        <TabsList className="w-full justify-normal rounded-none">
          <TabsTrigger className="flex gap-1" value="more-info">
            <Info className="size-4" /> Más información
          </TabsTrigger>
        </TabsList>
        <TabsContent value="more-info">
          <Accordion type="multiple" defaultValue={['activity']}>
            <AccordionItem value="activity">
              <AccordionTrigger className="px-3 border-b border-transparent data-[state=open]:border-border">
                Actividad
              </AccordionTrigger>
              <AccordionContent>
                <div className="flex flex-col gap-2 pt-2 pl-3 pr-2">
                  <DetailFieldRow label="Creado" value={formatHumanDateTime(project.createdAt)} />
                  <DetailFieldRow
                    label="Creado por"
                    value={<UserChip name={project.createdBy?.fullname} />}
                  />
                  <DetailFieldRow
                    label="Actualizado"
                    value={formatHumanDateTime(project.updatedAt)}
                  />
                  <DetailFieldRow
                    label="Actualizado por"
                    value={<UserChip name={project.updatedBy?.fullname} />}
                  />
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </TabsContent>
      </Tabs>

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar proyecto</AlertDialogTitle>
            <AlertDialogDescription>
              Se eliminará “{project.name}” ({project.code}). Sus subtipos, inmuebles y
              negociaciones históricas se conservan.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                deleteMutation.mutate(project._id, {
                  onSuccess: () => {
                    closePanel()
                  }
                })
              }}
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
